/**
 * LOP — PDP model size badge
 * Reads window.modelBadges (media.id -> text after '#') and updates
 * .model-badge overlays when the active gallery image changes, both in
 * the main gallery and in the PhotoSwipe lightbox (zoom) view.
 */
(function () {
  var PRODUCT_SELECTOR = '[data-section-type="product"]';
  var LIGHTBOX_CHILD_SELECTOR = '.product__media-item:not(.swiper-slide-duplicate) .lightbox-image';

  var scheduledSections = new Set();
  var rafId = null;
  var lightboxBadgeEl = null;
  var lightboxResizeHandler = null;
  var lightboxLoopId = null;
  var activeGalleryLinks = [];

  function getModelBadges() {
    return window.modelBadges || {};
  }

  function getBadgeTextForMediaId(mediaId) {
    if (!mediaId) return '';
    return getModelBadges()[mediaId] || getModelBadges()[String(mediaId)] || '';
  }

  function applyBadgeText(badge, text) {
    if (!badge) return;
    if (text) {
      badge.textContent = text;
      badge.removeAttribute('hidden');
    } else {
      badge.textContent = '';
      badge.setAttribute('hidden', '');
    }
  }

  // ---------- Main gallery badge (thumbnails / carousel) ----------

  function getActiveMediaIdFromCarousel(container) {
    var swiperInstance = container.swiper;
    if (
      swiperInstance &&
      Array.isArray(swiperInstance.slides) &&
      typeof swiperInstance.activeIndex === 'number'
    ) {
      var activeSlideEl = swiperInstance.slides[swiperInstance.activeIndex];
      if (activeSlideEl && activeSlideEl.dataset && activeSlideEl.dataset.mediaItemId) {
        return activeSlideEl.dataset.mediaItemId;
      }
    }

    var activeSlide = container.querySelector('.swiper-slide-active[data-media-item-id]');
    return activeSlide ? activeSlide.dataset.mediaItemId : null;
  }

  function getActiveMediaId(container) {
    var isCarousel = container.classList.contains('carousel') && container.classList.contains('swiper');

    if (isCarousel) {
      return getActiveMediaIdFromCarousel(container);
    }

    var activeItem = container.querySelector(
      '.product__media-item[data-product-media-wrapper]:not(.hidden)[data-media-item-id]'
    );
    return activeItem ? activeItem.dataset.mediaItemId : null;
  }

  function updateBadge(container) {
    var badge = container.querySelector('.model-badge[data-model-badge]');
    if (!badge) return;
    applyBadgeText(badge, getBadgeTextForMediaId(getActiveMediaId(container)));
  }

  function updateAllBadges(section) {
    section.querySelectorAll('.product__media-container').forEach(updateBadge);
  }

  function scheduleUpdate(section) {
    if (!section) return;
    scheduledSections.add(section);

    if (rafId) return;

    rafId = window.requestAnimationFrame(function () {
      rafId = null;
      scheduledSections.forEach(updateAllBadges);
      scheduledSections.clear();
    });
  }

  function bindSwiperInstance(container, section) {
    if (!container.swiper || container.swiper.__lopModelBadgeBound) return;
    container.swiper.__lopModelBadgeBound = true;
    container.swiper.on('slideChange', function () {
      scheduleUpdate(section);
    });
  }

  function watchForSwiperInstances(section) {
    var containers = section.querySelectorAll('.product__media-container.carousel.swiper');
    containers.forEach(function (container) {
      if (container.swiper) {
        bindSwiperInstance(container, section);
        return;
      }

      var attempts = 0;
      var intervalId = window.setInterval(function () {
        attempts += 1;
        if (container.swiper) {
          bindSwiperInstance(container, section);
          scheduleUpdate(section);
          window.clearInterval(intervalId);
        } else if (attempts > 40) {
          window.clearInterval(intervalId);
        }
      }, 250);
    });
  }

  function initSection(section) {
    if (section.dataset.lopModelBadgeInit === 'true') return;
    section.dataset.lopModelBadgeInit = 'true';

    updateAllBadges(section);
    watchForSwiperInstances(section);

    var observer = new MutationObserver(function () {
      scheduleUpdate(section);
      watchForSwiperInstances(section);
    });

    observer.observe(section, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  // ---------- Lightbox (PhotoSwipe) badge — DOM-first loop ----------

  function storeGalleryLinksFromClick(link) {
    var galleryRoot = link.closest('.lightbox-media-container');
    if (!galleryRoot) return;
    activeGalleryLinks = Array.prototype.slice.call(
      galleryRoot.querySelectorAll(LIGHTBOX_CHILD_SELECTOR)
    );
  }

  function getCounterIndex(pswpEl) {
    var counter = pswpEl.querySelector('.pswp__counter');
    if (!counter) return 0;
    var match = /(\d+)\s*\/\s*\d+/.exec(counter.textContent || '');
    return match ? parseInt(match[1], 10) - 1 : 0;
  }

  function getBadgeTextForLightboxIndex(index) {
    var link = activeGalleryLinks[index];
    if (!link) return '';

    if (link.dataset.lopModelBadge) {
      return link.dataset.lopModelBadge;
    }

    if (link.dataset.lopMediaId) {
      return getBadgeTextForMediaId(link.dataset.lopMediaId);
    }

    return '';
  }

  function getBadgeTextFromPswpInstance() {
    var pswp = window.pswp;
    if (!pswp || !pswp.currSlide || !pswp.currSlide.data) return '';

    var el = pswp.currSlide.data.element;
    if (!el) return '';

    if (el.dataset.lopModelBadge) {
      return el.dataset.lopModelBadge;
    }

    if (el.dataset.lopMediaId) {
      return getBadgeTextForMediaId(el.dataset.lopMediaId);
    }

    var mediaItem = el.closest('[data-media-item-id]');
    return mediaItem ? getBadgeTextForMediaId(mediaItem.dataset.mediaItemId) : '';
  }

  function getLightboxActiveImage(pswpEl) {
    if (window.pswp && window.pswp.currSlide && window.pswp.currSlide.container) {
      var pswpImg = window.pswp.currSlide.container.querySelector('.pswp__img');
      if (pswpImg) return pswpImg;
    }

    var index = getCounterIndex(pswpEl);
    var items = pswpEl.querySelectorAll('.pswp__item');
    if (items[index]) {
      var itemImg = items[index].querySelector('.pswp__img');
      if (itemImg) return itemImg;
    }

    return (
      pswpEl.querySelector('.pswp__item:not([aria-hidden="true"]) .pswp__img') ||
      pswpEl.querySelector('.pswp__img')
    );
  }

  function getOpenLightboxRoot() {
    return document.querySelector('.pswp.pswp--open');
  }

  function getLightboxBadge() {
    if (!lightboxBadgeEl) {
      lightboxBadgeEl = document.createElement('div');
      lightboxBadgeEl.className = 'model-badge';
      lightboxBadgeEl.setAttribute('data-model-badge-lightbox', '');
      lightboxBadgeEl.setAttribute('hidden', '');
    }
    return lightboxBadgeEl;
  }

  function clearLightboxBadgeStyles(badge) {
    if (!badge) return;
    badge.style.left = '';
    badge.style.top = '';
    badge.style.maxWidth = '';
  }

  function removeLightboxBadge() {
    if (!lightboxBadgeEl) return;
    if (lightboxBadgeEl.parentNode) {
      lightboxBadgeEl.parentNode.removeChild(lightboxBadgeEl);
    }
    clearLightboxBadgeStyles(lightboxBadgeEl);
    lightboxBadgeEl.setAttribute('hidden', '');
    lightboxBadgeEl.textContent = '';
  }

  function positionLightboxBadge(pswpEl) {
    var index = getCounterIndex(pswpEl);
    var text = getBadgeTextFromPswpInstance() || getBadgeTextForLightboxIndex(index);
    var badge = getLightboxBadge();
    var img = getLightboxActiveImage(pswpEl);

    if (!text || !img) {
      removeLightboxBadge();
      return;
    }

    if (badge.parentNode !== pswpEl) {
      pswpEl.appendChild(badge);
    }

    applyBadgeText(badge, text);

    var inset = 8;
    var imgRect = img.getBoundingClientRect();
    var rootRect = pswpEl.getBoundingClientRect();

    if (imgRect.width < 1 || imgRect.height < 1) {
      return;
    }

    var badgeHeight = badge.offsetHeight || 24;
    badge.style.left = imgRect.left - rootRect.left + inset + 'px';
    badge.style.top = imgRect.bottom - rootRect.top - badgeHeight - inset + 'px';
    badge.style.maxWidth = Math.max(0, imgRect.width - inset * 2) + 'px';
  }

  function stopLightboxLoop() {
    if (!lightboxLoopId) return;
    window.cancelAnimationFrame(lightboxLoopId);
    lightboxLoopId = null;
  }

  function startLightboxLoop() {
    stopLightboxLoop();

    function tick() {
      var pswpEl = getOpenLightboxRoot();
      if (!pswpEl) {
        stopLightboxLoop();
        removeLightboxBadge();
        return;
      }

      positionLightboxBadge(pswpEl);
      lightboxLoopId = window.requestAnimationFrame(tick);
    }

    lightboxLoopId = window.requestAnimationFrame(tick);
  }

  function bindLightboxResize() {
    if (lightboxResizeHandler) {
      window.removeEventListener('resize', lightboxResizeHandler);
    }
    lightboxResizeHandler = function () {
      var pswpEl = getOpenLightboxRoot();
      if (pswpEl) positionLightboxBadge(pswpEl);
    };
    window.addEventListener('resize', lightboxResizeHandler);
  }

  function initLightboxTracking() {
    bindLightboxResize();

    document.addEventListener(
      'click',
      function (event) {
        var link = event.target.closest && event.target.closest('.lightbox-image');
        if (!link) return;
        storeGalleryLinksFromClick(link);
        window.setTimeout(startLightboxLoop, 0);
        window.setTimeout(startLightboxLoop, 100);
        window.setTimeout(startLightboxLoop, 300);
      },
      true
    );

    var bodyObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          var isPswp = node.classList && node.classList.contains('pswp');
          var pswpRoot = isPswp ? node : node.querySelector && node.querySelector('.pswp');
          if (pswpRoot) startLightboxLoop();
        });
      });
    });
    bodyObserver.observe(document.body, { childList: true });
  }

  function init() {
    initLightboxTracking();

    if (!getModelBadges() || Object.keys(getModelBadges()).length === 0) return;

    document.querySelectorAll(PRODUCT_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
